import React from "react";
import { FeatureStatisticSection, StatisticFeatureCard } from "@/.storyblok/types/319751/storyblok-components";
import { SbBlokData, storyblokEditable } from "@storyblok/react";
import { renderRichText } from "@/lib/richText";
import Image from "next/image";
import Link from "next/link";

interface FeatureStatisticSectionProps {
  blok?: FeatureStatisticSection & SbBlokData;
}

const StatisticFeatureCardComponent: React.FC<{ blok: StatisticFeatureCard & SbBlokData; index?: number }> = ({
  blok,
  index = 0,
}) => {
  if (blok.type === "logo") {
    const getImageDimensions = (imageUrl: string, index: number) => {
      try {
        const originalDimensions = imageUrl.split("/")[5].split("x").map(Number);

        switch (index) {
          case 0:
            return { width: 300, height: 111 };
          case 1:
            return { width: 300, height: 110 };
          default:
            return { width: originalDimensions[0], height: originalDimensions[1] };
        }
      } catch (error) {
        console.error("Error parsing image dimensions:", error);
        return { width: 300, height: 100 };
      }
    };

    return (
      <div className="d-flex flex-column align-items-center-over-mobile" {...storyblokEditable(blok)}>
        <div>
          <p className="mb-3 mb-lg-4">{blok.title}</p>
          <div className="mb-3 mb-lg-4">
            {blok.logo?.filename && (
              <div>
                <Image
                  src={blok.logo.filename}
                  alt={blok.logo.alt || ""}
                  {...getImageDimensions(blok.logo.filename, index)}
                  className="mb-3 js-lazy-load"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  loading="lazy"
                  quality={75}
                />
              </div>
            )}
            {blok.cta_text && blok.cta_link?.url && (
              <Link
                href={blok.cta_link.url}
                target="_blank"
                rel="noopener"
                className="text-primary text-decoration-none"
              >
                <strong>{blok.cta_text}</strong>
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Default number type
  return (
    <div className="statistics-row text-center mb-4" {...storyblokEditable(blok)}>
      <div className="statistic-number brand-dark-text">
        <span dangerouslySetInnerHTML={{ __html: blok.number || "" }} />
        <p className="lead">{blok.title}</p>
      </div>
    </div>
  );
};

const FeatureStatisticSectionComponent: React.FC<FeatureStatisticSectionProps> = ({ blok }) => {
  if (!blok) return null;

  const { title, description, statistics, use_bottom_line } = blok || {};

  return (
    <>
      <div {...storyblokEditable(blok)} className="container py-4 py-md-5">
        <div className="row justify-content-center gy-4 gy-lg-0">
          <div className="col-lg-12 col-xl-12 col-xxl-13">
            {title && <h2>{title}</h2>}
            {description && (
              <div
                className="lead"
                dangerouslySetInnerHTML={{ __html: renderRichText(description) }}
              />
            )}
          </div>
          <div className="col-md-auto col-xl-10 offset-lg-1 offset-xl-1">
            {statistics?.map((stat) => (
              <StatisticFeatureCardComponent key={stat._uid} blok={stat as StatisticFeatureCard & SbBlokData} />
            ))}
          </div>
        </div>
      </div>
      {use_bottom_line && <hr />}
    </>
  );
};

export default FeatureStatisticSectionComponent;